import { createDeviceSyncPanel } from '../components/device-sync.js';
import { createScanCard } from '../components/scan-card.js';
import { createProductionSync } from '../sync/device-sync-deps.js';
import type { AccountDevice } from '../sync/device-sync.js';
import { ApiError, RequestAbortError, type CompanionPrefs } from '../../api/client.js';
import { h, toast, confirmModal, formModal, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import type { SessionController } from '../../session/controller.js';
import { getMobileBridge } from '../../platform/mobile-bridge.js';
import { DeviceUsbTransport, DeviceUsbCommandError, decodeCameraJpeg, type CameraPreviewResult, type CameraLiveResult, type ConnectionState, type DeviceUsbTransportOptions, type StatusResult } from '../../device-usb/index.js';

type UsbWindow = Window & { navigator: Window['navigator'] & { serial?: DeviceUsbTransportOptions['navigatorSerial'] } };

export function createDispositivosView(controller: SessionController): View {
  let active = true;
  let busy = false;
  let usb: DeviceUsbTransport | null = null;
  let usbState: ConnectionState = { kind: 'idle' };
  let currentStatus: StatusResult | null = null;
  let previewUrl: string | null = null;
  let cameraNoticeVersion = 0;
  const aborter = new AbortController();
  let usbAborter = new AbortController();
  const root = h('section', { class: 'view view-dispositivos', id: 'view-dispositivos' });
  root.appendChild(renderHeader('dispositivos'));
  const body = h('div', { class: 'section-body' });
  root.appendChild(body);
  const api = controller.getApi();
  const currentUser = controller.getUser?.() ?? null;
  // USB diagnostics need Web Serial or the Mac app, and never exist inside the native shells.
  const usbMounted = !getMobileBridge() && Boolean(window.asisDesktop || (window as UsbWindow).navigator.serial);
  const sync = createProductionSync(api, () => currentStatus?.deviceId ?? null);
  const accountDevices = () => sync.getState().account.devices;
  const syncPanel = createDeviceSyncPanel({
    api, sync, usbDiagnostics: usbMounted,
    accountName: currentUser?.displayName ?? '',
    currentUserId: currentUser?.id ?? '',
    onInvitation: () => { if (active) { advanced.open = true; joinDetails.open = true; joinCard.scrollIntoView?.({ block: 'nearest' }); joinInput.focus(); } },
    onRename: device => { void doRename(device); },
    onRevoke: device => { void doRevoke(device); },
    onRelease: device => { void doRelease(device); },
    openUsb: intent => { openUsbDiagnostics(intent); },
  });
  body.appendChild(syncPanel.el);
  const scanCard = createScanCard(api);
  body.appendChild(scanCard.el);

  // Account preference, not a claimed acknowledgement from an offline unit.
  if (typeof api.getCompanionPrefs === 'function') {
    let prefs: CompanionPrefs | null = null;
    const volume = h('input', { type: 'range', min: '0', max: '100', step: '1', disabled: true, 'aria-label': 'Volumen de tus unidades' });
    const value = h('output', {}, ['—']);
    const status = h('p', { class: 'card-sub', role: 'status' }, ['Cargando volumen…']);
    const save = h('button', { type: 'button', class: 'btn ghost', disabled: true }, ['Guardar volumen']);
    const card = h('section', { class: 'card device-volume' }, [h('div', { class: 'today-panel-head' }, [h('h2', { class: 'card-title' }, ['Volumen de tus unidades']), value]), volume, status, save]);
    body.append(h('details', { class: 'secondary-details device-volume-details' }, [h('summary', {}, ['Volumen de tus unidades']), card]));
    volume.addEventListener('input', () => { value.textContent = volume.value; save.disabled = !prefs || Number(volume.value) === prefs.volume; status.textContent = 'Cambio sin guardar'; });
    save.addEventListener('click', async () => {
      if (!prefs || save.disabled) return;
      save.disabled = true; volume.disabled = true; status.textContent = 'Guardando…';
      try {
        const latest = await api.getCompanionPrefs(aborter.signal);
        if (!active) return;
        const response = await api.updateCompanionPrefs({ ...latest.prefs, volume: Number(volume.value) }, aborter.signal);
        if (!active) return;
        prefs = response.prefs; volume.value = String(prefs.volume); value.textContent = volume.value;
        status.textContent = 'Guardado en tu cuenta. Se aplica cuando la unidad sincroniza.';
      } catch (error) { if (active) { status.textContent = 'No se pudo guardar. Vuelve a intentarlo.'; save.disabled = false; } }
      finally { if (active) volume.disabled = false; }
    });
    void api.getCompanionPrefs(aborter.signal).then(response => {
      if (!active) return;
      prefs = response.prefs; volume.value = String(prefs.volume); value.textContent = volume.value; volume.disabled = false;
      status.textContent = 'Se aplica a tus unidades al sincronizar.';
    }).catch(() => { if (active) status.textContent = 'Volumen no disponible. Vuelve a abrir esta pantalla para reintentar.'; });
  }

  const usbCard = h('section', { class: 'card usb-card' });
  const usbStateText = h('p', { class: 'card-sub', 'aria-live': 'polite' }, ['USB desconectado']);
  const usbActions = h('div', { class: 'form-row' });
  const connectButton = h('button', { class: 'btn ghost', type: 'button' }, ['Conectar por USB']);
  const disconnectButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Desconectar']);
  const statusButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Actualizar estado USB']);
  usbActions.append(connectButton, disconnectButton, statusButton);
  const deviceInfo = h('dl', { class: 'usb-info', hidden: true });
  const wifiFormHost = h('div', { class: 'usb-wifi-host' });
  const wifiForm = h('form', { class: 'form-stack', hidden: true });
  const ssid = h('input', { class: 'input', type: 'text', maxlength: '32', autocomplete: 'off', required: true, 'aria-label': 'Nombre de red Wi-Fi' });
  const password = h('input', { class: 'input', type: 'password', maxlength: '63', autocomplete: 'new-password', required: true, 'aria-label': 'Contraseña Wi-Fi' });
  const wifiSubmit = h('button', { class: 'btn primary', type: 'submit', disabled: true }, ['Guardar Wi-Fi']);
  wifiForm.append(h('label', { class: 'field' }, [h('span', {}, ['Red Wi-Fi']), ssid]), h('label', { class: 'field' }, [h('span', {}, ['Contraseña']), password]), wifiSubmit);
  const pairButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Vincular con mi cuenta']);
  const sdButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Comprobar tarjeta SD']);
  const sdNote = h('p', { class: 'card-sub' }, ['Comprobación de solo lectura. GPIO 38, 39 y 40 reservados para tarjeta SD.']);
  const audioToggle = h('input', { type: 'checkbox', disabled: true });
  const audioLabel = h('label', { class: 'field inline' }, [audioToggle, document.createTextNode('He conectado micrófono y altavoz según esquema')]);
  const audioDetails = h('details', { class: 'usb-wiring' });
  audioDetails.append(h('summary', {}, ['Cableado de audio']), h('p', { class: 'card-sub' }, ['INMP441: VDD 3.3 V, GND, L/R a GND, SCK 42, WS 14, SD 47. MAX98357A: VIN 5 V, GND, BCLK 42, LRC 14, DIN 41. Altavoz entre + y − del amplificador, nunca a GND. Audio se habilita solo tras confirmar cableado; no inicia conversación.']));
  const audioButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Habilitar audio']);
  const disableAudioButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Deshabilitar audio']);
  const stopConversationButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Detener conversación']);
  const audioActions = h('div', { class: 'form-row' }, [audioButton, disableAudioButton, stopConversationButton]);
  const setupActions = h('div', { class: 'form-row' }, [pairButton, sdButton]);
  usbCard.append(h('h2', { class: 'card-title' }, ['Dispositivo USB']), h('p', { class: 'card-sub' }, ['Conecta la placa ASIS con un cable USB de datos. Al conectar solo se comprueba el dispositivo; no se inicia ninguna conversación.']), usbStateText, usbActions, deviceInfo, wifiFormHost, setupActions, sdNote, audioLabel, audioDetails, audioActions);
  const cameraSection = h('section', { class: 'usb-camera', 'aria-label': 'Cámara del dispositivo' });
  const cameraStateText = h('p', { class: 'card-sub', 'aria-live': 'polite' }, ['Conecta el dispositivo para comprobar la cámara.']);
  const previewButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Tomar foto local']);
  const shareButton = h('button', { class: 'btn ghost', type: 'button', disabled: true }, ['Compartir foto nueva']);
  const cameraPrompt = h('input', { class: 'input', type: 'text', maxlength: '384', autocomplete: 'off', disabled: true, 'aria-label': 'Pregunta sobre la foto (opcional)' });
  const cameraImage = h('img', { class: 'usb-camera-preview', alt: 'Última foto local de la cámara del dispositivo', width: '320', height: '240', hidden: true });
  const cameraResultText = h('p', { class: 'card-sub', 'aria-live': 'polite' });
  cameraImage.addEventListener('error', () => {
    if (!active || !previewUrl) return;
    clearPreview(); cameraResultText.textContent = 'No se pudo mostrar la foto recibida.';
  });
  cameraSection.append(h('h3', { class: 'card-title' }, ['Cámara']), h('p', { class: 'card-sub' }, ['La foto local se conserva solo en la memoria de esta pantalla. Compartir toma otra foto y la envía a la sesión de Gemini ya iniciada en el dispositivo.']), cameraStateText, previewButton,
    h('label', { class: 'field' }, [h('span', {}, ['Pregunta sobre la foto (opcional)']), cameraPrompt]), shareButton, cameraImage, cameraResultText);
  usbCard.append(cameraSection);
  const usbDetails = h('details', { class: 'usb-diagnostics' });
  usbDetails.append(h('summary', {}, ['Diagnóstico opcional por USB']), usbCard);

  const pairCard = h('section', { class: 'card' });
  pairCard.append(h('h2', { class: 'card-title' }, ['Vincular con un código']), h('p', { class: 'card-sub' }, [`Usa esta opción si el dispositivo ya te ha dado un código temporal de 8 caracteres. Para una unidad nueva, pulsa Sincronizar y utiliza su código privado de alta.${usbMounted ? ' En una placa sin pantalla puedes usar «Vincular con mi cuenta» desde la conexión USB.' : ''}`]));
  const codeForm = h('form', { class: 'form-row' });
  const codeInput = h('input', { type: 'text', class: 'input', placeholder: 'Código de 8 caracteres', maxlength: '8', autocomplete: 'off', 'aria-label': 'Código de emparejamiento' });
  const nameInput = h('input', { type: 'text', class: 'input', placeholder: 'Nombre (ej. Cocina)', maxlength: '40', 'aria-label': 'Nombre del dispositivo' });
  const claimBtn = h('button', { type: 'submit', class: 'btn ghost' }, ['Vincular']);
  codeForm.append(codeInput, nameInput, claimBtn); pairCard.appendChild(codeForm);

  const joinCard = h('section', { class: 'card' });
  joinCard.append(h('h2', { class: 'card-title' }, ['Acceder a un dispositivo compartido']), h('p', { class: 'card-sub' }, ['Si alguien te ha compartido un dispositivo por invitación, introduce el código que te haya pasado en persona.']));
  const joinForm = h('form', { class: 'form-stack' });
  const joinInput = h('input', { type: 'text', class: 'input', maxlength: '128', autocomplete: 'off', required: true, 'aria-label': 'Código de invitación al dispositivo compartido', placeholder: 'Código de invitación' });
  const joinSubmit = h('button', { type: 'submit', class: 'btn ghost' }, ['Aceptar invitación']);
  const joinStatus = h('p', { class: 'card-sub', 'aria-live': 'polite' });
  const joinError = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  joinForm.append(h('label', { class: 'field' }, [h('span', {}, ['Código de invitación']), joinInput]), h('div', { class: 'form-row' }, [joinSubmit]));
  joinCard.append(joinForm, joinStatus, joinError);

  const pairDetails = h('details', { class: 'secondary-details' });
  pairDetails.append(h('summary', {}, ['Vincular con un código temporal']), pairCard);
  const joinDetails = h('details', { class: 'secondary-details' }, [h('summary', {}, ['Acceder a un dispositivo compartido']), joinCard]);
  const advanced = h('details', { class: 'secondary-details device-setup-advanced' }, [h('summary', {}, ['Avanzado · otras formas de conectar']), joinDetails, pairDetails]);
  if (usbMounted) advanced.appendChild(usbDetails);
  const discoveryDetails = syncPanel.el.querySelector('.sync-advanced');
  if (discoveryDetails) advanced.querySelector('summary')!.after(discoveryDetails);
  body.appendChild(advanced);

  function setBusy(value: boolean): void {
    busy = value;
    updateControls();
  }
  function updateControls(): void {
    const connected = usbState.kind === 'connected';
    connectButton.disabled = busy || connected || ['requesting', 'opening', 'handshaking'].includes(usbState.kind);
    disconnectButton.disabled = !['requesting', 'opening', 'handshaking', 'connected'].includes(usbState.kind);
    statusButton.disabled = busy || !connected;
    wifiSubmit.disabled = busy || !connected;
    pairButton.disabled = busy || !connected;
    sdButton.disabled = busy || !connected;
    audioToggle.disabled = busy || !connected || currentStatus?.audioEnabled === true;
    audioButton.disabled = busy || !connected || !audioToggle.checked || currentStatus?.audioEnabled === true;
    disableAudioButton.disabled = busy || !connected || currentStatus?.audioEnabled !== true;
    stopConversationButton.disabled = busy || !connected || currentStatus?.sessionActive !== true;
    codeInput.disabled = busy;
    nameInput.disabled = busy;
    claimBtn.disabled = busy;
    previewButton.disabled = busy || !canCapture('preview');
    shareButton.disabled = busy || !canCapture('live');
    cameraPrompt.disabled = busy || !canCapture('live');
    cameraStateText.textContent = !connected ? 'Conecta el dispositivo para comprobar la cámara.'
      : currentStatus?.cameraSupported !== true ? 'Este firmware no informa de una cámara compatible.'
      : currentStatus.cameraBusy ? 'La cámara está ocupada.'
      : currentStatus.sessionActive === false ? 'Cámara disponible para una foto local. Para compartir hace falta una sesión de Gemini iniciada en el dispositivo.'
      : canCapture('live') ? 'Sesión Gemini lista para recibir una foto nueva.'
      : 'La foto local requiere detener la sesión del dispositivo. Compartir requiere una sesión de Gemini lista y compatible. Actualiza el estado si ha cambiado.';
  }
  function canCapture(target: 'preview' | 'live'): boolean {
    if (usbState.kind !== 'connected' || currentStatus?.cameraSupported !== true || currentStatus.cameraBusy === true || currentStatus.capturing === true) return false;
    return target === 'preview' ? currentStatus.sessionActive === false
      : currentStatus.sessionActive === true && currentStatus.sessionReady === true && currentStatus.provider === 'gemini-live' && currentStatus.cameraLiveSupported !== false;
  }
  function clearPreview(): void {
    cameraImage.removeAttribute('src'); cameraImage.hidden = true;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = null;
  }
  function renderUsbState(state: ConnectionState): void {
    if (!active) return;
    usbState = state;
    if (state.kind === 'connected') {
      currentStatus = state.status;
      usbStateText.textContent = `USB conectado · ${state.status.wifiConnected ? `Wi-Fi ${state.status.ip || 'conectada'}` : 'Wi-Fi desconectada'}`;
      deviceInfo.hidden = false;
      deviceInfo.replaceChildren(...[
        ['Dispositivo', state.status.deviceId], ['Firmware', state.status.firmware], ['Protocolo', String(state.status.protocol)], ['Memoria flash', formatBytes(state.status.flashBytes)],
        ['PSRAM', formatBytes(state.status.psramBytes)], ['Fase', state.status.phase], ['Audio habilitado', state.status.audioEnabled ? 'Sí' : 'No'],
      ].flatMap(([label, value]) => [h('dt', {}, [label ?? '']), h('dd', {}, [value ?? ''])]));
      wifiFormHost.replaceChildren(wifiForm);
      wifiForm.hidden = false;
      audioToggle.checked = false;
    } else {
      currentStatus = null;
      if (state.kind === 'closed' || state.kind === 'failed') { usbAborter.abort(); busy = false; }
      clearPreview(); cameraPrompt.value = ''; cameraResultText.textContent = '';
      usbStateText.textContent = state.kind === 'requesting' ? 'Elige puerto USB compatible…' : state.kind === 'opening' ? 'Abriendo conexión USB…' : state.kind === 'handshaking' ? 'Verificando firmware ASIS…' : state.kind === 'failed' ? state.reason : 'USB desconectado';
      deviceInfo.hidden = true;
      wifiForm.hidden = true;
      wifiForm.remove();
      password.value = '';
      audioToggle.checked = false;
    }
    updateControls();
    if (state.kind === 'connected' || state.kind === 'closed' || state.kind === 'failed') void sync.refreshUsb();
  }
  function ensureTransport(): DeviceUsbTransport | null {
    if (usb) return usb;
    const serial = (window as UsbWindow).navigator.serial;
    if (!serial) { toast('Este navegador no permite conexión USB serie. Abre ASIS Desktop.', 'error'); return null; }
    usb = new DeviceUsbTransport({ navigatorSerial: serial });
    usb.onState(renderUsbState);
    usb.onEvent(event => {
      if (!active || usbAborter.signal.aborted || event.event !== 'camera_error') return;
      cameraNoticeVersion++;
      if (event.code === 'unsupported_image') {
        if (currentStatus) currentStatus = { ...currentStatus, cameraLiveSupported: false };
        cameraResultText.textContent = 'La sesión ha rechazado las imágenes. No se compartirán más fotos en esta sesión.';
        updateControls();
      } else cameraResultText.textContent = 'El servicio ha limitado las fotos. Espera y vuelve a intentarlo cuando quieras.';
    });
    return usb;
  }
  function openUsbDiagnostics(intent: 'diagnose' | 'connect'): void {
    if (!usbMounted) return;
    advanced.open = true; usbDetails.open = true;
    usbDetails.scrollIntoView?.({ block: 'start' });
    if (intent === 'connect') void connectUsb();
    else (usbDetails.querySelector('summary') as HTMLElement | null)?.focus();
  }
  async function doRename(device: AccountDevice): Promise<void> {
    await formModal({ title: 'Cambiar nombre', fields: [{ name: 'name', label: 'Nombre del dispositivo', value: device.name }], onSave: async values => {
      if (!values.name) throw new Error('El nombre no puede estar vacío.');
      const result = await api.patchDevice(device.id, { name: values.name }, aborter.signal);
      if (!active) return;
      sync.applyAccount(accountDevices().map(item => item.id === device.id ? result.device : item)); toast('Nombre actualizado', 'ok');
    } });
  }
  async function doRevoke(device: AccountDevice): Promise<void> {
    const confirmed = await confirmModal({ title: 'Revocar dispositivo', body: `Vas a revocar ${device.name}. Sus credenciales y sesiones activas se revocarán.`, confirmLabel: 'Revocar', danger: true });
    if (!confirmed || !active) return;
    try { await api.deleteDevice(device.id, aborter.signal); if (!active) return; sync.applyAccount(accountDevices().filter(item => item.id !== device.id)); toast('Dispositivo revocado', 'ok'); }
    catch (error) { if (active && !(error instanceof RequestAbortError)) toast('No se pudo revocar el dispositivo.', 'error'); }
  }
  async function doRelease(device: AccountDevice): Promise<void> {
    const confirmed = await confirmModal({ title: 'Liberar dispositivo', body: `${device.name} dejará de estar en tu cuenta, pero no se revoca: en menos de un minuto mostrará un código nuevo en su pantalla para volver a vincularlo. Conserva su Wi-Fi y deja de estar compartido con otras personas.`, confirmLabel: 'Liberar' });
    if (!confirmed || !active) return;
    try { await api.releaseDevice(device.id, aborter.signal); if (!active) return; sync.applyAccount(accountDevices().filter(item => item.id !== device.id)); toast('Dispositivo liberado: mostrará un código nuevo', 'ok'); }
    catch (error) { if (active && !(error instanceof RequestAbortError)) toast('No se pudo liberar el dispositivo.', 'error'); }
  }
  async function claim(code: string, name?: string, signal = aborter.signal): Promise<void> {
    const result = await api.claimDevice({ code, name }, signal);
    if (!active || signal.aborted) return;
    sync.applyAccount([result.device, ...accountDevices().filter(device => device.id !== result.device.id)]);
    codeInput.value = ''; nameInput.value = ''; toast('Dispositivo vinculado a tu cuenta.', 'ok');
  }

  async function connectUsb(): Promise<void> {
    const transport = ensureTransport(); if (!transport || busy || connectButton.disabled) return;
    usbAborter = new AbortController();
    setBusy(true);
    try { await transport.connect(); }
    catch (error) { if (active && !(error instanceof DeviceUsbCommandError && error.code === 'cancelled')) toast(error instanceof DeviceUsbCommandError ? error.message : 'No se pudo conectar por USB.', 'error'); }
    finally { if (active) setBusy(false); }
  }
  connectButton.addEventListener('click', () => { void connectUsb(); });
  disconnectButton.addEventListener('click', async () => { if (!usb) return; usbAborter.abort(); password.value = ''; cameraPrompt.value = ''; clearPreview(); setBusy(true); try { await usb.disconnect(); } finally { if (active) setBusy(false); } });
  statusButton.addEventListener('click', async () => {
    if (!usb || busy) return; setBusy(true);
    try { await usb.send('status'); } catch { if (active && !usbAborter.signal.aborted) toast('No se pudo actualizar el estado USB.', 'error'); }
    finally { if (active) setBusy(false); }
  });
  async function captureCamera(target: 'preview' | 'live'): Promise<void> {
    if (!usb || busy || !canCapture(target)) return;
    const signal = usbAborter.signal;
    const noticeVersion = cameraNoticeVersion;
    const prompt = target === 'live' ? cameraPrompt.value.trim() : '';
    cameraPrompt.value = ''; clearPreview(); cameraResultText.textContent = '';
    setBusy(true);
    try {
      const fields = prompt ? { target, prompt } : { target };
      const result = await usb.send<CameraPreviewResult | CameraLiveResult>('camera.capture', fields);
      if (!active || signal.aborted || noticeVersion !== cameraNoticeVersion) return;
      if (result.target === 'preview') {
        const blob = new Blob([decodeCameraJpeg(result.data)], { type: 'image/jpeg' });
        previewUrl = URL.createObjectURL(blob);
        cameraImage.src = previewUrl; cameraImage.hidden = false;
        cameraResultText.textContent = 'Foto local · 320 × 240. Se borrará al desconectar o salir de esta pantalla.';
      } else cameraResultText.textContent = 'Foto enviada desde el dispositivo. La recepción o el análisis aún no están confirmados.';
    } catch (error) {
      if (active && !signal.aborted) cameraResultText.textContent = error instanceof DeviceUsbCommandError ? error.message : 'No se pudo tomar la foto. Revisa el estado del dispositivo y la pregunta.';
    } finally { if (active && !signal.aborted) setBusy(false); }
  }
  previewButton.addEventListener('click', () => { void captureCamera('preview'); });
  shareButton.addEventListener('click', () => { void captureCamera('live'); });
  wifiForm.addEventListener('submit', async event => {
    event.preventDefault(); if (!usb || busy || !currentStatus) return;
    let psk = password.value;
    const network = ssid.value.trim();
    password.value = '';
    if (!network || !psk) { psk = ''; toast('Indica red y contraseña Wi-Fi.', 'error'); return; }
    setBusy(true);
    try { await usb.send('wifi.set', { ssid: network, psk }); if (!active || usbAborter.signal.aborted) return; toast('Wi-Fi conectada y guardada.', 'ok'); await usb.send('status'); }
    catch { if (active) toast('No se pudo configurar la red Wi-Fi.', 'error'); }
    finally { psk = ''; if (active) setBusy(false); }
  });
  pairButton.addEventListener('click', async () => {
    if (!usb || busy) return; setBusy(true);
    try {
      const result = await usb.send<{ paired?: boolean; code?: string; expiresInMs?: number }>('pair');
      if (!active) return;
      if (result.paired) { toast('Este dispositivo ya está vinculado. La cuenta actual no se ha verificado.', 'info'); }
      else if (result.code) { await claim(result.code, undefined, usbAborter.signal); }
      else toast('El dispositivo no devolvió un código de vinculación.', 'error');
      await sync.refreshAccount();
      if (active && !usbAborter.signal.aborted) await usb.send('status');
    } catch { if (active) toast('No se pudo iniciar la vinculación.', 'error'); }
    finally { if (active) setBusy(false); }
  });
  sdButton.addEventListener('click', async () => {
    if (!usb || busy) return; setBusy(true);
    try { const result = await usb.send<{ available: boolean; type: string; capacityBytes: number; totalBytes: number; usedBytes: number }>('sd.check'); if (active) toast(result.available ? `Tarjeta ${result.type || 'disponible'} · ${formatBytes(result.capacityBytes)}` : 'No se detectó una tarjeta SD.', result.available ? 'ok' : 'info'); }
    catch { if (active) toast('No se pudo comprobar la tarjeta SD.', 'error'); }
    finally { if (active) setBusy(false); }
  });
  audioToggle.addEventListener('change', updateControls);
  audioButton.addEventListener('click', async () => {
    if (!usb || busy || !audioToggle.checked) return; setBusy(true);
    try { await usb.send('audio.enable', { enabled: true }); if (active) { toast('Audio habilitado. No se ha iniciado una conversación.', 'ok'); await usb.send('status'); } }
    catch { if (active) toast('No se pudo habilitar el audio.', 'error'); }
    finally { if (active) setBusy(false); }
  });
  stopConversationButton.addEventListener('click', async () => {
    if (!usb || busy || currentStatus?.sessionActive !== true) return;
    const signal = usbAborter.signal;
    setBusy(true);
    try {
      await usb.send('session.stop');
      if (!active || signal.aborted) return;
      await usb.send('status');
      if (active && !signal.aborted) toast('Conversación del dispositivo detenida.', 'ok');
    } catch { if (active && !signal.aborted) toast('No se pudo confirmar la parada. Actualiza el estado USB.', 'error'); }
    finally { if (active && !signal.aborted) setBusy(false); }
  });
  disableAudioButton.addEventListener('click', async () => {
    if (!usb || busy || currentStatus?.audioEnabled !== true) return;
    const signal = usbAborter.signal;
    setBusy(true);
    try {
      await usb.send('audio.enable', { enabled: false });
      if (!active || signal.aborted) return;
      await usb.send('status');
      if (active && !signal.aborted) toast('Audio del dispositivo deshabilitado.', 'ok');
    } catch { if (active && !signal.aborted) toast('No se pudo deshabilitar el audio. Actualiza el estado USB.', 'error'); }
    finally { if (active && !signal.aborted) setBusy(false); }
  });
  codeForm.addEventListener('submit', async event => {
    event.preventDefault(); if (busy) return;
    const code = codeInput.value.trim();
    if (code.length !== 8) { toast('El código debe tener 8 caracteres.', 'error'); return; }
    setBusy(true);
    try { await claim(code, nameInput.value.trim() || undefined); }
    catch (error) { if (active && !(error instanceof RequestAbortError)) toast(`No se pudo vincular: ${apiMessage(error)}`, 'error'); }
    finally { if (active) setBusy(false); }
  });
  joinForm.addEventListener('submit', async event => {
    event.preventDefault(); if (busy) return;
    const code = joinInput.value.trim();
    if (!active) return;
    if (!code) { joinError.textContent = 'Introduce el código de invitación.'; joinError.hidden = false; return; }
    if (code.length > 128) { joinError.textContent = 'El código es demasiado largo.'; joinError.hidden = false; return; }
    joinError.hidden = true; joinError.textContent = '';
    joinSubmit.disabled = true; joinInput.disabled = true;
    joinStatus.textContent = 'Aceptando invitación…';
    setBusy(true);
    try {
      const result = await api.joinDevice({ code }, aborter.signal);
      if (!active || aborter.signal.aborted) return;
      joinInput.value = '';
      joinStatus.textContent = `Te has unido a ${result.device.name}.`;
      // Show it at once (only on a list that loaded), then let the server's list have the last word.
      if (sync.getState().account.status === 'ready') sync.applyAccount([result.device, ...accountDevices().filter(item => item.id !== result.device.id)]);
      await sync.refreshAccount({ silent: true });
      if (active) toast('Acceso añadido al dispositivo.', 'ok');
    } catch (error) {
      if (!active || aborter.signal.aborted) return;
      if (error instanceof RequestAbortError) return;
      joinStatus.textContent = '';
      joinError.textContent = apiMessage(error);
      joinError.hidden = false;
    } finally {
      if (!active) return;
      joinSubmit.disabled = false; joinInput.disabled = false;
      if (active) setBusy(false);
    }
  });

  void sync.refreshAccount();
  return {
    el: root,
    destroy() { active = false; scanCard.destroy?.(); syncPanel.destroy?.(); sync.destroy(); aborter.abort(); usbAborter.abort(); password.value = ''; ssid.value = ''; cameraPrompt.value = ''; clearPreview(); joinInput.value = ''; joinStatus.textContent = ''; joinError.textContent = ''; joinError.hidden = true; if (usb) void usb.destroy(); usb = null; },
    update() { void sync.refreshAccount({ silent: true }); scanCard.update?.({}); },
  };
}

function apiMessage(error: unknown): string { return error instanceof ApiError ? error.message : 'Error de comunicación.'; }
function formatBytes(value: number): string { return Number.isFinite(value) && value >= 0 ? `${(value / 1024 / 1024).toFixed(1)} MB` : 'No disponible'; }
